import { Filter as CoreFilter, IProduct } from '@kizi/comparator-core';
import _ from 'lodash';
import {
  AVAILABLE_FILTERS,
  FILTER,
  FILTERS_TYPE,
  FILTER_FIELDS,
  FILTER_LABELS,
  FILTER_TOOLTIP,
  INTERNET_FILTERS,
} from '.';
import { Filter, FiltersProps } from '../../components/Filters';
import { COMPARATOR_TYPE } from '../../funnels';
import { sortArrayByKey } from '../global';
import { EnergyProduct } from '../product';

type Filters = { [key: string]: any[] };

export const isFilteropties = (value, id) =>
  Array.isArray(value) && id === 'filteropties';

export const getValues = (filterValue) =>
  filterValue.reduce((acc, current): any => {
    if (current.value === 'Yes' || current.value === 'Warning') {
      return [...acc, current.description];
    }

    return [...acc];
  }, []);

export const extraFiltering = (
  filterName,
  selectedFilters,
  productFilterValue
) => {
  switch (filterName) {
    case 'monthlyPrice':
    case 'downloadSpeed':
      return !(
        productFilterValue >= selectedFilters[0] &&
        productFilterValue <= selectedFilters[1]
      );
    case 'signal':
      if (
        selectedFilters.includes('dsl') &&
        (productFilterValue === 'vdsl' || productFilterValue === 'adsl')
      ) {
        return false;
      }
      return !selectedFilters.includes(productFilterValue);

    case 'specificTvChannel':
      return !selectedFilters
        .flatMap((v) => v)
        .map((el) => el.label)
        .every((v) => productFilterValue.includes(v));

    default:
      return true;
  }
};

export const extraAdding = (filter, filterValue, updatedProduct) => {
  switch (filter.id) {
    case 'extraOptions':
      return filter.fields
        .filter(({ key }) => _.get(updatedProduct, key ?? ''))
        .map(({ value }) => value);
    case 'bellen':
      return filterValue === 1;
    case 'channelsPackages':
      return filterValue?.map((val) => val.name) ?? [];
    case 'installation':
      return filterValue?.some((val) => val.available) ?? [];
    case 'deliveryTime':
      return updatedProduct.contractInformation?.deliveryTime.text;
    case 'onceOffCosts':
      return updatedProduct.onceOffCosts?.connectionCosts;
    default:
      return filterValue;
  }
};

export function addFiltersToProducts(
  filters: CoreFilter[],
  products: IProduct[]
): IProduct[] {
  let updatedProducts: IProduct[] = [];

  for (const product of products) {
    let _filters = [...filters];
    let updatedProduct = {
      ...product,
      filters: {
        ...product.filters,
      },
    };

    for (const filter of _filters) {
      if (filter.id !== 'showPriceMode' && filter.id !== 'maxOwnRisk') {
        let filterValue = _.get(updatedProduct, filter.key);

        // If key given from layout/overview is a whole array and we want to transform the values
        if (isFilteropties(filterValue, filter.id)) {
          filterValue = getValues(filterValue);
        }

        if (updatedProduct.type === 'tripleplay') {
          filterValue = extraAdding(filter, filterValue, updatedProduct);
        }

        if (filter['filterById']) {
          filterValue = _.get(updatedProduct, filter.id);
        }

        updatedProduct.filters[filter.id] = filterValue;
      }
    }
    updatedProducts.push(updatedProduct);
  }

  return updatedProducts;
}

export const findCommonElements = (productFilterValue, selectedFilters) => {
  if (Array.isArray(productFilterValue)) {
    return !selectedFilters.every((item) => productFilterValue.includes(item));
  }

  return true;
};

export const shouldProductBeExcluded = (
  filterName,
  selectedFilters,
  productFilterValue
) =>
  selectedFilters.length > 0 &&
  selectedFilters[0] !== 'all' &&
  !selectedFilters.includes(productFilterValue) &&
  !selectedFilters.includes('' + productFilterValue) &&
  extraFiltering(filterName, selectedFilters, productFilterValue) &&
  findCommonElements(productFilterValue, selectedFilters);

const hasWaitingTime = (selectedFilters, productFilterValue): boolean =>
  selectedFilters.includes('BEOORDELING') &&
  (productFilterValue === 'BEOORDELING' ||
    productFilterValue === 'GEEN_SELECTIE_OF_WACHTTIJD');

export const filterProducts = (
  products: IProduct[],
  filters: FiltersProps['choices']
): IProduct[] => {
  let updatedProducts: IProduct[] = [];
  const _filters = { ...filters };
  const _products = [...products];
  for (const product of _products) {
    const shouldExcludeInternet =
      product.type === 'tripleplay' &&
      !_filters.productTypes?.includes('internet');

    if (shouldExcludeInternet) {
      delete product.filters[INTERNET_FILTERS.DOWNLOAD_SPEED];
      delete product.filters[INTERNET_FILTERS.SIGNAL];
    }

    const shouldBeAddedList = [Boolean(Object.keys(product.filters).length)];
    for (const filterName in product.filters) {
      const productFilterValue = product.filters[filterName];
      const selectedFilters = _filters[filterName] ?? [];

      let shouldExclude;
      if (
        filterName === 'strictestLevelNewInsured' &&
        hasWaitingTime(selectedFilters, productFilterValue)
      ) {
        shouldExclude = false;
      } else if (
        product.type === 'tripleplay' &&
        filterName === 'deliveryTime'
      ) {
        if (Number(productFilterValue) > selectedFilters[0]) {
          shouldExclude = true;
        }
      } else {
        shouldExclude = shouldProductBeExcluded(
          filterName,
          selectedFilters,
          productFilterValue
        );
      }

      if (shouldExclude) {
        shouldBeAddedList.push(false);
      }
    }

    if (!shouldBeAddedList.includes(false)) updatedProducts.push(product);
    if (_filters.maxOwnRisk && product.type === 'travel') {
      updatedProducts = filterProductsByPrice(updatedProducts, _filters);
    }
  }

  return updatedProducts;
};

function filterProductsByPrice(updatedProducts, _filters) {
  let filterValue;
  switch (_filters.maxOwnRisk[0]) {
    case '1':
      filterValue = 0;
      break;
    case '2':
      filterValue = 50;
      break;
    case '3':
      filterValue = 100;
      break;
    case '4':
      filterValue = 150;
      break;
    case '5':
      filterValue = 200;
      break;
    case '6':
      filterValue = 250;
      break;
    default:
      filterValue = Number.MAX_SAFE_INTEGER;
  }
  updatedProducts = updatedProducts.filter(
    (upadatedProduct) =>
      upadatedProduct.bigProductFeature.value.month <= filterValue
  );
  return updatedProducts;
}

function getFiltersFromProducts(data: any[]): Filters {
  let filters = {};

  for (const product of data) {
    for (const filter in product.filters) {
      if (!filters[filter]) {
        filters = {
          ...filters,
          [filter]: [],
        };
      }

      const val = product.filters[filter];

      if (!filters[filter].includes(val)) {
        filters[filter].push(val);
      }
    }
  }

  return filters;
}

function initialiseFilterGroup(filter: string, type: COMPARATOR_TYPE): Filter {
  return {
    fields: FILTER_FIELDS[type][filter] || [],
    id: filter,
    title: AVAILABLE_FILTERS[type][filter] || '',
    type: FILTERS_TYPE[filter] || 'checkbox',
    tooltip: FILTER_TOOLTIP[type]?.[filter] || '',
  };
}

function populateFilterGroup(
  filterGroup: Filter,
  currentFilter: any[],
  filter: string
): void {
  if (filterGroup.fields.length === 0 && currentFilter) {
    for (const filterItem of currentFilter) {
      const label = FILTER_LABELS[filter]?.(filterItem) || filterItem;

      if (label) {
        filterGroup.fields.push({
          label,
          value: filterItem,
        });
      }
    }
  }
}

export function prepareFiltersObject(
  data: EnergyProduct[],
  type: COMPARATOR_TYPE
): {
  choices: Filters;
  groups: Filter[];
} {
  const filters = getFiltersFromProducts(data);

  let choices: Filters = {
    [FILTER.SHOW_PRICE_MODE]: ['month'],
  };

  let groups: Filter[] = [
    {
      id: 'showPriceMode',
      fields: [
        {
          label: 'Maand',
          value: 'month',
        },
        {
          label: 'Jaar',
          value: 'year',
        },
      ],
      title: 'Toon prijs per:',
      tooltip: '',
      type: 'radio-group',
    },
  ];

  for (const filter in AVAILABLE_FILTERS[type]) {
    const currentFilter = filters[filter];

    if (currentFilter) {
      const filterGroup: Filter = initialiseFilterGroup(filter, type);

      populateFilterGroup(filterGroup, currentFilter, filter);

      choices[filter] = [];

      groups.push(sortGroup(filterGroup));
    }
  }

  return {
    choices,
    groups,
  };
}

function sortGroup(filterGroup: Filter): Filter {
  if (
    filterGroup.id === FILTER.CONTRACT_TYPE ||
    filterGroup.id === FILTER.GREEN_POWER ||
    filterGroup.id === FILTER.SUSTAINABILITY_LABEL
  ) {
    return filterGroup;
  } else {
    return {
      ...filterGroup,
      fields: sortArrayByKey(filterGroup.fields, 'label'),
    };
  }
}
